import { Trans } from 'react-i18next';

import {
   HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledQuery,
   HealthcareProviderAppointmentConfirmationPatientNotificationEnabledSetCommand,
} from '@dreryk/egabinet-phone-notification-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { useFormController } from '@dreryk/react-common';
import {
   dispatchSnackbarPositiveEvent,
   IconFillColor,
   IconSize,
   IconWrapper,
   QuestionmarkCircleIcon,
   StyledFlex,
   Toggle,
   Tooltip,
} from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { patientPhoneNotificationTranslationKeys } from '../../translations';

import { PatientPhoneNotificationAppointmentConfirmationToggleLoader } from './PatientPhoneNotificationAppointmentConfirmationToggle.loader';
import { PhoneNotificationTooltipText } from './PhoneNotificationTooltipText';

const SAVED_SNACKBAR_DELAY_IN_MS = 5000;
const useCurrentHealthcareProviderAppointmentConfirmationPatientNotificationEnabledSetCommand = (
   onSuccess: VoidFunction
): UseCqrsCommandResult<void, PatientPhoneNotificationAppointmentConfirmationToggleFormBean> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new HealthcareProviderAppointmentConfirmationPatientNotificationEnabledSetCommand();
         command.enabled = Choice.of(parameters.notificationEnabled ?? false);
         return command;
      },
      {
         onSuccess: (ignore, parameters) => {
            dispatchSnackbarPositiveEvent(
               <>
                  <Trans
                     i18nKey={
                        parameters.notificationEnabled
                           ? patientPhoneNotificationTranslationKeys.notificationToggle
                                .appointmentConfirmationToggleSaveOn
                           : patientPhoneNotificationTranslationKeys.notificationToggle
                                .appointmentConfirmationToggleSaveOff
                     }
                  />
               </>,
               {
                  autoHideDelayInMs: SAVED_SNACKBAR_DELAY_IN_MS,
               }
            );
            onSuccess();
            refetchCqrsQuery(HealthcareProviderAppointmentCancellationTokenInConfirmationEnabledQuery);
         },
      }
   );
};

type PatientPhoneNotificationAppointmentConfirmationToggleFormBean = {
   notificationEnabled: boolean;
};

export const PatientPhoneNotificationAppointmentConfirmationToggle = (props: {
   readonly notificationEnabled: boolean;
   readonly messages: string[];
   readonly refetch: VoidFunction;
}): JSX.Element | null => {
   const { register, handleSubmit } = useFormController<PatientPhoneNotificationAppointmentConfirmationToggleFormBean>(
      {
         notificationEnabled: props.notificationEnabled,
      },
      { mode: 'onChange' }
   );
   const {
      executeAsync: onSubmit,
      loading,
   } = useCurrentHealthcareProviderAppointmentConfirmationPatientNotificationEnabledSetCommand(props.refetch);

   return (
      <StyledFlex gap={'12px'}>
         <form onInput={handleSubmit(onSubmit)}>
            <Toggle
               controller={register('notificationEnabled')}
               testId={'patientPhoneNotificationAppointmentConfirmationToggle'}
               disabled={loading}
            />
         </form>
         <StyledFlex gap={'4px'} alignItems={'center'}>
            <Trans i18nKey={patientPhoneNotificationTranslationKeys.notificationToggle.appointmentConfirmationToggle} />
            <Tooltip
               maxWidth={'500px'}
               testId={'appointmentConfirmationToggleTooltip'}
               content={
                  <PhoneNotificationTooltipText
                     header={
                        patientPhoneNotificationTranslationKeys.notificationToggle.appointmentConfirmationToggleTooltip
                     }
                     messages={props.messages}
                  />
               }
            >
               <IconWrapper
                  testId={'appointmentConfirmationToggleTooltipIconWrapper'}
                  fillColor={IconFillColor.PRIMARY}
                  size={IconSize.SMALL}
               >
                  <QuestionmarkCircleIcon />
               </IconWrapper>
            </Tooltip>
         </StyledFlex>
      </StyledFlex>
   );
};

export const PatientPhoneNotificationAppointmentConfirmationToggleWithLoader = (): JSX.Element => {
   return (
      <PatientPhoneNotificationAppointmentConfirmationToggleLoader>
         {({ notificationEnabled, messages, refetch }) => (
            <PatientPhoneNotificationAppointmentConfirmationToggle
               notificationEnabled={notificationEnabled}
               messages={messages}
               refetch={refetch}
            />
         )}
      </PatientPhoneNotificationAppointmentConfirmationToggleLoader>
   );
};
